import React from "react";
import { AlertTriangle, RefreshCw } from "lucide-react";

const TableEditForm = ({
  newItem,
  setNewItem,
  savedQueries,
  setLoader,
  getData,
  getThemeURL,
  setDataSourcesForTable,
  handleDateFieldsUpdate,
  selectedBankIds,
}) => {
  const [noDataForDateRange, setNoDataForDateRange] = React.useState(false);

  console.log("TableEditForm received props:", {
    selectedBankIds,
    type: typeof selectedBankIds,
    isArray: Array.isArray(selectedBankIds),
    length: selectedBankIds?.length,
  });

  // Helper function to determine filter fields dynamically
  const determineFilterFields = (queryObject) => {
    // Extract table name from entity
    const entity = queryObject.entity || "";
    const tableName = entity.includes(".") ? entity.split(".")[1] : entity;

    // Get joins information
    const joins = queryObject.joins || [];
    const joinedTables = joins.map((join) => {
      const table = join.table || join.alias || "";
      return table.includes(".") ? table.split(".")[1] : table;
    });

    // Helper function to determine filter field names
    let bankFilterField = null;

    // Check what tables are available
    const hasBankTable = tableName === "bank" || joinedTables.includes("bank");
    const hasJoins = joins.length > 0;

    console.log("TableEditForm: Table analysis:", {
      tableName,
      joinedTables,
      hasBankTable,
      hasJoins,
    });

    // Determine bank filter field
    if (tableName === "bank") {
      // If entity is bank table
      bankFilterField = "bank.id";
    } else if (hasBankTable && hasJoins) {
      // If joins are there and bank table is available
      bankFilterField = "bank.id";
    } else if (
      !hasBankTable &&
      tableName !== "bank" &&
      tableName !== "branch_details"
    ) {
      // For core tables (not bank or branch_details), use table.bank_id
      bankFilterField = `${tableName}.bank_id`;
    } else if (!hasBankTable) {
      // If no bank table available, use current table with bank_id
      bankFilterField = `${tableName}.bank_id`;
    }

    console.log(
      "TableEditForm: Determined bank filter field:",
      bankFilterField
    );

    return { bankFilterField };
  };

  // Function to identify date/timestamp fields from the data
  const getDateFields = () => {
    if (!newItem.data || newItem.data.length === 0) {
      return [];
    }

    const dateFields = [];
    const sampleRow = newItem.data[0];

    Object.keys(sampleRow).forEach((key) => {
      const value = sampleRow[key];

      // Check if the value looks like a date or timestamp
      if (typeof value === "string") {
        // Check for various date formats
        const datePatterns = [
          /^\d{4}-\d{2}-\d{2}$/, // YYYY-MM-DD (date)
          /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/, // ISO timestamp
          /^\d{4}-\d{2}-\d{2}\s\d{2}:\d{2}:\d{2}/, // YYYY-MM-DD HH:MM:SS
        ];

        const isDateField = datePatterns.some((pattern) => pattern.test(value));

        if (isDateField) {
          // Determine the full column name (with table prefix)
          const tableName = newItem.dataSource
            ? savedQueries.find((q) => q.serviceName === newItem.dataSource)
                ?.queryBuilder
            : null;

          let fullColumnName = key;
          if (tableName) {
            try {
              const queryObject =
                typeof tableName === "string"
                  ? JSON.parse(tableName)
                  : tableName;
              const tablePrefix = queryObject.entity.split(".")[1];
              fullColumnName = `${tablePrefix}.${key}`;
            } catch (error) {
              console.warn("Could not parse table name:", error);
            }
          }

          dateFields.push({
            key: key,
            fullName: fullColumnName,
            label: key
              .replace(/([A-Z])/g, " $1")
              .replace(/^./, (str) => str.toUpperCase()),
            value: value,
            type:
              value.includes("T") || value.includes(" ") ? "timestamp" : "date",
          });
        }
      }
    });

    return dateFields;
  };

  // In handleRefreshWithDateFilter function
  const handleRefreshWithDateFilter = async () => {
    if (
      !newItem.dataSource ||
      !newItem.fromDate ||
      !newItem.toDate ||
      !newItem.selectedDateField
    ) {
      return;
    }

    setLoader(true);

    const matchedQuery = savedQueries.find(
      (q) => q.serviceName === newItem.dataSource
    );

    if (!matchedQuery) {
      setLoader(false);
      return;
    }

    const queryObject =
      typeof matchedQuery.queryBuilder === "string"
        ? JSON.parse(matchedQuery.queryBuilder)
        : matchedQuery.queryBuilder;

    const filters = {};

    try {
      const userResponse = await getData(getThemeURL);
      if (
        userResponse &&
        Array.isArray(userResponse) &&
        userResponse.length > 0
      ) {
        const user = userResponse[0];

        // **NEW**: Apply dynamic filter logic for date filter refresh
        if (user.role_name === "SUPER ADMIN") {
          // For Super Admin, use selectedBankIds if available
          if (selectedBankIds && selectedBankIds.length > 0) {
            // **NEW**: Use dynamic filter field determination
            const { bankFilterField } = determineFilterFields(queryObject);

            if (bankFilterField) {
              filters[bankFilterField] = {
                operator: "IN",
                value: selectedBankIds.map(Number),
              };
              console.log(
                "Applied dynamic bank filter for Super Admin in date filter:",
                filters[bankFilterField]
              );
            }
          }
        } else {
          // For non-Super Admin users, use their specific IDs with dynamic field determination
          const { bankFilterField } = determineFilterFields(queryObject);

          if (user.bank_id && bankFilterField) {
            filters[bankFilterField] = {
              operator: "IN",
              value: [user.bank_id], // Convert single ID to array format
            };
            console.log(
              "Applied dynamic bank filter for regular user in date filter:",
              filters[bankFilterField]
            );
          }
        }
      }
    } catch (error) {
      console.error("Error fetching user profile for filters:", error);
    }

    // Create date filter with timestamp handling
    const dateFilter = {};

    // Check if the selected field is a timestamp type
    const selectedFieldData = dateFields.find(
      (field) => field.fullName === newItem.selectedDateField
    );
    const isTimestamp =
      selectedFieldData && selectedFieldData.type === "timestamp";
    handleDateFieldsUpdate(dateFields);

    if (isTimestamp) {
      // Append timestamp to fromDate and toDate for timestamp fields
      const fromDateTime = newItem.fromDate + " 00:00:00.000";
      const toDateTime = newItem.toDate + " 23:59:59.999"; // Use end of day for toDate

      dateFilter[newItem.selectedDateField] = {
        operator: "BETWEEN",
        value: [fromDateTime, toDateTime],
      };
    } else {
      // Use date values as-is for date-only fields
      dateFilter[newItem.selectedDateField] = {
        operator: "BETWEEN",
        value: [newItem.fromDate, newItem.toDate],
      };
    }

    const enhancedQuery = {
      ...queryObject,
      filters: {
        ...queryObject.filters,
        ...filters,
        ...dateFilter,
      },
    };

    console.log(
      "Query sent to /nifi/fetchQuery with date filter:",
      enhancedQuery
    );
    console.log("Applied filters with date:", { ...filters, ...dateFilter });

    try {
      const response = await fetch("/nifi/fetchQuery", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(enhancedQuery),
      });

      const result = await response.json();

      const rawData = result.data || [];

      if (rawData.length === 0) {
        setNoDataForDateRange(true);
        return;
      } else {
        setNoDataForDateRange(false);
      }

      // **KEY CHANGE**: Extract column names directly from data fields
      const dataColumns = rawData.length > 0 ? Object.keys(rawData[0]) : [];

      // Transform data - convert date fields from ISO format to YYYY-MM-DD
      const transformedData = rawData.map((row) => {
        const transformedRow = { ...row };

        Object.keys(transformedRow).forEach((key) => {
          const value = transformedRow[key];

          if (
            key.toLowerCase().includes("created_on") ||
            (typeof value === "string" &&
              /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/.test(value))
          ) {
            try {
              const date = new Date(value);
              if (!isNaN(date.getTime())) {
                transformedRow[key] = date.toISOString().split("T")[0];
              }
            } catch (error) {
              console.warn(`Failed to transform date for field ${key}:`, error);
            }
          }
        });

        return transformedRow;
      });

      console.log("Transformed data with date filter:", transformedData);

      // **NEW CODE**: Preserve existing column configuration, only update data
      const updatedColumns = newItem.columns.map((existingCol) => {
        // Check if this column still exists in the new data
        if (dataColumns.includes(existingCol.key)) {
          return {
            ...existingCol, // Preserve all existing settings including alignment
            // Only update type if needed based on new data
            type:
              typeof transformedData[0]?.[existingCol.key] === "number"
                ? "number"
                : existingCol.key.toLowerCase().includes("created_on")
                ? "date"
                : "text",
          };
        }
        return existingCol;
      });

      // Add any new columns that weren't in the original set
      const newColumns = dataColumns
        .filter(
          (col) =>
            !newItem.columns.some((existingCol) => existingCol.key === col)
        )
        .map((col, index) => ({
          key: col,
          label: col
            .replace(/([A-Z_])/g, " $1")
            .replace(/^./, (str) => str.toUpperCase())
            .replace(/\s+/g, " ")
            .trim(),
          type:
            typeof transformedData[0]?.[col] === "number"
              ? "number"
              : col.toLowerCase().includes("created_on")
              ? "date"
              : "text",
          selected: true,
          order: newItem.columns.length + index + 1,
          alignment: "left", // Default for truly new columns only
        }));

      const finalColumns = [...updatedColumns, ...newColumns];

      // Similarly for subColumns if it's an expandable table
      const updatedSubColumns =
        newItem.tableType === "expandable" && newItem.subColumns.length > 0
          ? newItem.subColumns.map((existingCol) => {
              if (dataColumns.includes(existingCol.key)) {
                return {
                  ...existingCol, // Preserve all existing settings including alignment
                  type:
                    typeof transformedData[0]?.[existingCol.key] === "number"
                      ? "number"
                      : existingCol.key.toLowerCase().includes("created_on")
                      ? "date"
                      : "text",
                };
              }
              return existingCol;
            })
          : newItem.tableType === "expandable"
          ? dataColumns.map((col, index) => ({
              key: col,
              label: col
                .replace(/([A-Z_])/g, " $1")
                .replace(/^./, (str) => str.toUpperCase())
                .replace(/\s+/g, " ")
                .trim(),
              type:
                typeof transformedData[0]?.[col] === "number"
                  ? "number"
                  : col.toLowerCase().includes("created_on")
                  ? "date"
                  : "text",
              selected: true,
              order: index + 1,
              alignment: "left",
            }))
          : [];

      // **UPDATED**: Use preserved columns instead of recreating them
      setNewItem({
        ...newItem,
        data: transformedData,
        fromDate: newItem.fromDate,
        toDate: newItem.toDate,
        dateFilterApplied: true,
        columns: finalColumns, // Use preserved columns with alignment settings
        subColumns: updatedSubColumns, // Use preserved subColumns with alignment settings
      });

      setDataSourcesForTable((prev) => ({
        ...prev,
        [newItem.dataSource]: {
          data: transformedData,
          columns: dataColumns, // **KEY CHANGE**: Use dataColumns
          defaultTableType: "basic",
        },
      }));
    } catch (error) {
      console.error("Failed to execute query with date filter:", error);
    } finally {
      setLoader(false);
    }
  };

  const labelStyle = {
    display: "block",
    marginBottom: "8px",
    fontSize: "14px",
    fontWeight: "bold",
    color: "#374151",
  };

  // Get available date fields
  const dateFields = getDateFields();

  return (
    <>
      {/* Color picker and table type sections remain the same */}
      <div
        style={{
          display: "flex",
          gap: "16px",
          marginBottom: "16px",
          flexWrap: "wrap",
        }}
      >
        {/* Main header color pickers - always visible */}
        <div style={{ minWidth: "120px" }}>
          <label
            style={{
              display: "block",
              marginBottom: "8px",
              fontSize: "14px",
              fontWeight: "bold",
            }}
          >
            Header Background
          </label>
          <input
            type="color"
            value={newItem.headerColor || "#f9fafb"}
            onChange={(e) =>
              setNewItem({
                ...newItem,
                headerColor: e.target.value,
              })
            }
            style={{
              width: "50px",
              height: "40px",
              border: "1px solid #d1d5db",
              borderRadius: "4px",
              cursor: "pointer",
            }}
          />
        </div>
        <div style={{ minWidth: "120px" }}>
          <label
            style={{
              display: "block",
              marginBottom: "8px",
              fontSize: "14px",
              fontWeight: "bold",
              marginLeft: "60px",
            }}
          >
            Header Text
          </label>
          <input
            type="color"
            value={newItem.headerTextColor || "#374151"}
            onChange={(e) =>
              setNewItem({
                ...newItem,
                headerTextColor: e.target.value,
              })
            }
            style={{
              width: "50px",
              height: "40px",
              border: "1px solid #d1d5db",
              borderRadius: "4px",
              cursor: "pointer",
              marginLeft: "60px",
            }}
          />
        </div>
        {/* Sub-header color pickers - only for expandable tables */}
        {newItem.tableType === "expandable" && (
          <div
            style={{
              display: "flex",
              gap: "20px",
              alignItems: "flex-end",
            }}
          >
            <div style={{ minWidth: "120px" }}>
              <label
                style={{
                  display: "block",
                  marginBottom: "8px",
                  fontSize: "14px",
                  fontWeight: "bold",
                  marginLeft: "97px",
                }}
              >
                Sub-Header Background
              </label>
              <input
                type="color"
                value={newItem.subHeaderColor || "#f1f5f9"}
                onChange={(e) =>
                  setNewItem({
                    ...newItem,
                    subHeaderColor: e.target.value,
                  })
                }
                style={{
                  width: "50px",
                  height: "40px",
                  border: "1px solid #d1d5db",
                  borderRadius: "4px",
                  cursor: "pointer",
                  marginLeft: "97px",
                }}
              />
            </div>
            <div style={{ minWidth: "120px" }}>
              <label
                style={{
                  display: "block",
                  marginBottom: "8px",
                  fontSize: "14px",
                  fontWeight: "bold",
                }}
              >
                Sub-Header Text
              </label>
              <input
                type="color"
                value={newItem.subHeaderTextColor || "#334155"}
                onChange={(e) =>
                  setNewItem({
                    ...newItem,
                    subHeaderTextColor: e.target.value,
                  })
                }
                style={{
                  width: "50px",
                  height: "40px",
                  border: "1px solid #d1d5db",
                  borderRadius: "4px",
                  cursor: "pointer",
                }}
              />
            </div>
          </div>
        )}
      </div>

      {/* Table Type selection */}
      <div>
        <label
          style={{
            display: "block",
            marginBottom: "8px",
            fontSize: "14px",
            fontWeight: "bold",
          }}
        >
          Table Type
        </label>
        <div
          style={{
            display: "flex",
            gap: "8px",
            marginBottom: "16px",
          }}
        >
          <button
            onClick={() => {
              setNewItem({
                ...newItem,
                tableType: "basic",
                columns: [],
                dataSource: "",
                subColumns: [],
                data: [],
                dateFilter: null,
                fromDate: "",
                toDate: "",
                selectedDateField: "",
              });
            }}
            style={{
              flex: 1,
              padding: "12px",
              borderRadius: "8px",
              border: `1px solid ${
                newItem.tableType === "basic" ? "#1a7a73" : "#e5e7eb"
              }`,
              backgroundColor:
                newItem.tableType === "basic" ? "#f0f9ff" : "white",
              cursor: "pointer",
            }}
          >
            <div style={{ fontWeight: "500", color: "#0f4a45" }}>
              Basic Table
            </div>
            <div style={{ fontSize: "12px", color: "#64748b" }}>
              Simple table without expandable rows
            </div>
          </button>
          <button
            onClick={() => {
              setNewItem({
                ...newItem,
                dataSource: "",
                tableType: "expandable",
                columns: [],
                subColumns: [],
                data: [],
                dateFilter: null,
                fromDate: "",
                toDate: "",
                selectedDateField: "",
              });
            }}
            style={{
              flex: 1,
              padding: "12px",
              borderRadius: "8px",
              border: `1px solid ${
                newItem.tableType === "expandable" ? "#1a7a73" : "#e5e7eb"
              }`,
              backgroundColor:
                newItem.tableType === "expandable" ? "#f0f9ff" : "white",
              cursor: "pointer",
            }}
          >
            <div style={{ fontWeight: "500", color: "#0f4a45" }}>
              Expandable Table
            </div>
            <div style={{ fontSize: "12px", color: "#64748b" }}>
              Rows can be expanded for more details
            </div>
          </button>
        </div>
      </div>

      {/* Data source selection - without date filter */}
      <div style={{ marginBottom: "16px" }}>
        <div style={{ minWidth: "382px" }}>
          <label
            style={{
              display: "block",
              marginBottom: "8px",
              fontSize: "14px",
              fontWeight: "bold",
            }}
          >
            Data Source <span style={{ color: "red" }}>*</span>
          </label>
          <select
            value={newItem.dataSource || ""}
            onChange={async (e) => {
              const serviceName = e.target.value;
              setLoader(true);
              console.log(savedQueries, "SAVED QUERIES");
              const matchedQuery = savedQueries.find(
                (q) => q.serviceName === serviceName
              );

              if (!matchedQuery) {
                setLoader(false);
                return;
              }

              const queryObject =
                typeof matchedQuery.queryBuilder === "string"
                  ? JSON.parse(matchedQuery.queryBuilder)
                  : matchedQuery.queryBuilder;

              const filters = {};

              try {
                const userResponse = await getData(getThemeURL);
                if (
                  userResponse &&
                  Array.isArray(userResponse) &&
                  userResponse.length > 0
                ) {
                  const user = userResponse[0];

                  // Check if user is Super Admin
                  if (user.role_name === "SUPER ADMIN") {
                    // For Super Admin, use selectedBankIds if available
                    if (selectedBankIds && selectedBankIds.length > 0) {
                      // **NEW**: Use dynamic filter field determination
                      const { bankFilterField } =
                        determineFilterFields(queryObject);

                      if (bankFilterField) {
                        filters[bankFilterField] = {
                          operator: "IN",
                          value: selectedBankIds.map(Number),
                        };
                        console.log(
                          "Applied dynamic bank filter for Super Admin:",
                          filters[bankFilterField]
                        );
                      }
                    }
                  } else {
                    // For non-Super Admin users, use their specific IDs with dynamic field determination
                    const { bankFilterField } =
                      determineFilterFields(queryObject);

                    if (user.bank_id && bankFilterField) {
                      filters[bankFilterField] = {
                        operator: "IN",
                        value: [user.bank_id], // Convert single ID to array format
                      };
                      console.log(
                        "Applied dynamic bank filter for regular user:",
                        filters[bankFilterField]
                      );
                    }
                  }
                }
              } catch (error) {
                console.error(
                  "Error fetching user profile for filters:",
                  error
                );
              }

              const enhancedQuery = {
                ...queryObject,
                filters: {
                  ...queryObject.filters,
                  ...filters,
                },
              };

              console.log("Query sent to /nifi/fetchQuery:", enhancedQuery);
              console.log("Applied filters:", filters);

              try {
                const response = await fetch("/nifi/fetchQuery", {
                  method: "POST",
                  headers: {
                    "Content-Type": "application/json",
                  },
                  body: JSON.stringify(enhancedQuery),
                });

                const result = await response.json();
                console.log("Table data Mapping", result);

                const rawData = result.data || [];

                // **KEY CHANGE**: Extract column names directly from data fields instead of using result.columns
                const dataColumns =
                  rawData.length > 0 ? Object.keys(rawData[0]) : [];

                console.log("Extracted columns from data:", dataColumns);

                // Transform data - convert date fields from ISO format to YYYY-MM-DD
                const transformedData = rawData.map((row) => {
                  const transformedRow = { ...row };

                  Object.keys(transformedRow).forEach((key) => {
                    const value = transformedRow[key];

                    if (
                      key.toLowerCase().includes("date") ||
                      (typeof value === "string" &&
                        /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/.test(value))
                    ) {
                      try {
                        const date = new Date(value);
                        if (!isNaN(date.getTime())) {
                          transformedRow[key] = date
                            .toISOString()
                            .split("T")[0];
                        }
                      } catch (error) {
                        console.warn(
                          `Failed to transform date for field ${key}:`,
                          error
                        );
                      }
                    }
                  });

                  return transformedRow;
                });

                console.log("Transformed data:", transformedData);

                setNewItem({
                  ...newItem,
                  dataSource: serviceName,
                  tableType: newItem.tableType || "basic",
                  data: transformedData,
                  fromDate: "",
                  toDate: "",
                  selectedDateField: "",
                  dateFilterApplied: false,
                  dateFieldsChanged: false,
                  // **KEY CHANGE**: Use dataColumns instead of cleanColumns
                  columns: dataColumns.map((col, index) => ({
                    key: col,
                    label: col
                      .replace(/([A-Z_])/g, " $1")
                      .replace(/^./, (str) => str.toUpperCase())
                      .replace(/\s+/g, " ")
                      .trim(),
                    type:
                      typeof transformedData[0]?.[col] === "number"
                        ? "number"
                        : col.toLowerCase().includes("date")
                        ? "date"
                        : "text",
                    selected: true,
                    order: index + 1,
                    alignment: "left", // Default alignment
                  })),
                  subColumns:
                    newItem.tableType === "expandable"
                      ? dataColumns.map((col, index) => ({
                          key: col,
                          label: col
                            .replace(/([A-Z_])/g, " $1")
                            .replace(/^./, (str) => str.toUpperCase())
                            .replace(/\s+/g, " ")
                            .trim(),
                          type:
                            typeof transformedData[0]?.[col] === "number"
                              ? "number"
                              : col.toLowerCase().includes("date")
                              ? "date"
                              : "text",
                          selected: true,
                          order: index + 1,
                          alignment: "left", // Default alignment
                        }))
                      : [],
                  dateFilter: null,
                });

                setDataSourcesForTable((prev) => ({
                  ...prev,
                  [serviceName]: {
                    data: transformedData,
                    columns: dataColumns, // **KEY CHANGE**: Use dataColumns
                    defaultTableType: "basic",
                  },
                }));
              } catch (error) {
                console.error("Failed to execute selected query:", error);
              } finally {
                setLoader(false);
              }
            }}
            required={true}
            style={{
              width: "95%",
              padding: "10px 12px",
              borderRadius: "8px",
              border: `1px solid ${newItem.dataSource ? "#d1d5db" : "#ef4444"}`,
              backgroundColor: "white",
              fontSize: "14px",
              color: "#374151",
              outline: "none",
            }}
          >
            <option value="">Select data source</option>
            {savedQueries.map((query) => (
              <option key={query.id} value={query.serviceName}>
                {query.serviceName}
              </option>
            ))}
          </select>
          {!newItem.dataSource && (
            <span
              style={{
                color: "#ef4444",
                fontSize: "12px",
                marginTop: "4px",
                display: "block",
              }}
            >
              Data source is required
            </span>
          )}
        </div>
      </div>

      {/* Column selection */}
      {newItem.dataSource && (
        <>
          <div>
            <label
              style={{
                display: "block",
                marginBottom: "8px",
                fontSize: "14px",
                fontWeight: "bold",
              }}
            >
              Main Columns
            </label>

            {/* Column Headers */}
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "30px 1fr 1fr 80px 50px", // Added alignment column
                gap: "8px",
                padding: "8px",
                borderBottom: "1px solid #e5e7eb",
                fontWeight: "600",
                fontSize: "12px",
                color: "#64748b",
              }}
            >
              <span></span>
              <span style={{ marginLeft: "7px" }}>Labels</span>
              <span style={{ marginLeft: "-2px" }}>Fields</span>
              <span style={{ marginLeft: "-5px" }}>Alignment</span>
              <span style={{ marginLeft: "-10px" }}>Order</span>
            </div>

            <div
              style={{
                border: "1px solid #e5e7eb",
                borderRadius: "0 0 8px 8px",
                maxHeight: "200px",
                overflowY: "auto",
              }}
            >
              {newItem.columns
                .map((column, index) => ({
                  ...column,
                  originalIndex: index,
                }))
                .sort((a, b) => (a.order || 0) - (b.order || 0))
                .map((column, displayIndex) => (
                  <div
                    key={column.key}
                    style={{
                      display: "grid",
                      gridTemplateColumns: "30px 1fr 1fr 80px 50px", // Added alignment column
                      alignItems: "center",
                      gap: "8px",
                      padding: "8px",
                      backgroundColor:
                        displayIndex % 2 === 0 ? "#f9fafb" : "white",
                    }}
                  >
                    {/* Checkbox */}
                    <input
                      type="checkbox"
                      checked={column.selected}
                      onChange={(e) => {
                        const isChecked = e.target.checked;
                        const updatedColumns = [...newItem.columns];
                        const columnIndex = column.originalIndex;

                        // Update the selected status
                        updatedColumns[columnIndex].selected = isChecked;

                        if (isChecked) {
                          // When checking: assign the next available order among selected columns
                          const currentlySelected = updatedColumns.filter(
                            (col) =>
                              col.selected &&
                              col !== updatedColumns[columnIndex]
                          );
                          const maxOrder =
                            currentlySelected.length > 0
                              ? Math.max(
                                  ...currentlySelected.map(
                                    (col) => col.order || 0
                                  )
                                )
                              : 0;
                          updatedColumns[columnIndex].order = maxOrder + 1;
                        } else {
                          // When unchecking: find the correct position at the bottom
                          const remainingSelected = updatedColumns.filter(
                            (col) =>
                              col.selected &&
                              col !== updatedColumns[columnIndex]
                          );

                          // Get all currently unselected columns (excluding the one being unchecked now)
                          const currentlyUnselected = updatedColumns.filter(
                            (col) =>
                              !col.selected &&
                              col !== updatedColumns[columnIndex]
                          );

                          // The new position should be after all selected columns and after existing unselected columns
                          const selectedCount = remainingSelected.length;
                          const unselectedCount = currentlyUnselected.length;
                          const newPosition =
                            selectedCount + unselectedCount + 1;

                          updatedColumns[columnIndex].order = newPosition;

                          // Reorder remaining selected columns to be sequential (1, 2, 3, etc.)
                          remainingSelected
                            .sort((a, b) => (a.order || 0) - (b.order || 0))
                            .forEach((col, index) => {
                              const colIndex = updatedColumns.findIndex(
                                (c) => c.key === col.key
                              );
                              if (colIndex !== -1) {
                                updatedColumns[colIndex].order = index + 1;
                              }
                            });
                        }

                        setNewItem({
                          ...newItem,
                          columns: updatedColumns,
                        });
                      }}
                    />

                    {/* Label */}
                    <input
                      type="text"
                      value={column.label}
                      onChange={(e) => {
                        const updatedColumns = [...newItem.columns];
                        updatedColumns[column.originalIndex].label =
                          e.target.value;
                        setNewItem({
                          ...newItem,
                          columns: updatedColumns,
                        });
                      }}
                      placeholder="Column label"
                      style={{
                        padding: "6px 8px",
                        borderRadius: "4px",
                        border: "1px solid #d1d5db",
                        fontSize: "12px",
                        width: "90%",
                      }}
                    />

                    {/* Field - Disabled input */}
                    <input
                      type="text"
                      value={column.key}
                      disabled
                      style={{
                        padding: "6px 8px",
                        borderRadius: "4px",
                        border: "1px solid #d1d5db",
                        fontSize: "12px",
                        backgroundColor: "#f3f4f6",
                        color: "#6b7280",
                        cursor: "not-allowed",
                        width: "90%",
                      }}
                    />

                    {/* Alignment Selector */}
                    <select
                      value={column.alignment || "left"}
                      onChange={(e) => {
                        const updatedColumns = [...newItem.columns];
                        updatedColumns[column.originalIndex].alignment =
                          e.target.value;
                        setNewItem({
                          ...newItem,
                          columns: updatedColumns,
                        });
                      }}
                      style={{
                        padding: "4px",
                        borderRadius: "4px",
                        border: "1px solid #d1d5db",
                        fontSize: "11px",
                        width: "75px",
                      }}
                    >
                      <option value="left">Left</option>
                      <option value="center">Center</option>
                      <option value="right">Right</option>
                    </select>

                    {/* Order */}
                    <select
                      value={column.order || 1}
                      onChange={(e) => {
                        const newOrder = parseInt(e.target.value);
                        const updatedColumns = [...newItem.columns];

                        // Find if another column has this order
                        const existingColWithOrder = updatedColumns.find(
                          (c) => c.order === newOrder && c.key !== column.key
                        );

                        if (existingColWithOrder) {
                          // Swap orders
                          existingColWithOrder.order = column.order;
                        }

                        updatedColumns[column.originalIndex].order = newOrder;
                        setNewItem({
                          ...newItem,
                          columns: updatedColumns,
                        });
                      }}
                      style={{
                        padding: "4px",
                        borderRadius: "4px",
                        border: "1px solid #d1d5db",
                        fontSize: "12px",
                      }}
                    >
                      {/* Show options for ALL columns, maintaining sequential order */}
                      {Array.from(
                        { length: newItem.columns.length },
                        (_, idx) => (
                          <option key={idx + 1} value={idx + 1}>
                            {idx + 1}
                          </option>
                        )
                      )}
                    </select>
                  </div>
                ))}
            </div>
          </div>

          {/* Sub-columns for expandable tables */}
          {newItem.tableType === "expandable" &&
            newItem.subColumns.length > 0 && (
              <div style={{ marginBottom: "24px" }}>
                <label
                  style={{
                    display: "block",
                    marginBottom: "8px",
                    fontSize: "14px",
                    fontWeight: "bold",
                  }}
                >
                  Sub Columns (Expanded View)
                </label>
                {/* Column Headers */}
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "30px 1fr 1fr 80px 50px", // Added alignment column
                    gap: "8px",
                    padding: "8px",
                    borderBottom: "1px solid #e5e7eb",
                    fontWeight: "600",
                    fontSize: "12px",
                    color: "#64748b",
                  }}
                >
                  <span></span>
                  <span style={{ marginLeft: "7px" }}>Labels</span>
                  <span style={{ marginLeft: "-2px" }}>Fields</span>
                  <span style={{ marginLeft: "-5px" }}>Alignment</span>
                  <span style={{ marginLeft: "-10px" }}>Order</span>
                </div>

                <div
                  style={{
                    border: "1px solid #e5e7eb",
                    borderRadius: "0 0 8px 8px",
                    maxHeight: "200px",
                    overflowY: "auto",
                  }}
                >
                  {newItem.subColumns
                    .map((column, index) => ({
                      ...column,
                      originalIndex: index,
                    }))
                    .sort((a, b) => (a.order || 0) - (b.order || 0))
                    .map((column, displayIndex) => {
                      const columnLabel =
                        column.label ||
                        column.key
                          .replace(/([A-Z])/g, " $1")
                          .replace(/^./, (str) => str.toUpperCase());

                      return (
                        <div
                          key={column.key}
                          style={{
                            display: "grid",
                            gridTemplateColumns: "30px 1fr 1fr 80px 50px", // Added alignment column
                            alignItems: "center",
                            gap: "8px",
                            padding: "8px",
                            backgroundColor:
                              displayIndex % 2 === 0 ? "#f9fafb" : "white",
                          }}
                        >
                          {/* Checkbox */}
                          <input
                            type="checkbox"
                            checked={column.selected}
                            onChange={(e) => {
                              const isChecked = e.target.checked;
                              const updatedSubColumns = [...newItem.subColumns];
                              const columnIndex = column.originalIndex;

                              // Update the selected status
                              updatedSubColumns[columnIndex].selected =
                                isChecked;

                              if (isChecked) {
                                // When checking: find the next available order among selected columns
                                const currentlySelected =
                                  updatedSubColumns.filter(
                                    (col) =>
                                      col.selected &&
                                      col !== updatedSubColumns[columnIndex]
                                  );
                                const maxOrder =
                                  currentlySelected.length > 0
                                    ? Math.max(
                                        ...currentlySelected.map(
                                          (col) => col.order || 0
                                        )
                                      )
                                    : 0;
                                updatedSubColumns[columnIndex].order =
                                  maxOrder + 1;
                              } else {
                                // When unchecking: find the correct position at the bottom
                                const remainingSelected =
                                  updatedSubColumns.filter(
                                    (col) =>
                                      col.selected &&
                                      col !== updatedSubColumns[columnIndex]
                                  );

                                // Get all currently unselected columns (excluding the one being unchecked now)
                                const currentlyUnselected =
                                  updatedSubColumns.filter(
                                    (col) =>
                                      !col.selected &&
                                      col !== updatedSubColumns[columnIndex]
                                  );

                                // The new position should be after all selected columns and after existing unselected columns
                                const selectedCount = remainingSelected.length;
                                const unselectedCount =
                                  currentlyUnselected.length;
                                const newPosition =
                                  selectedCount + unselectedCount + 1;

                                updatedSubColumns[columnIndex].order =
                                  newPosition;

                                // Reorder remaining selected columns to be sequential (1, 2, 3, etc.)
                                remainingSelected
                                  .sort(
                                    (a, b) => (a.order || 0) - (b.order || 0)
                                  )
                                  .forEach((col, index) => {
                                    const colIndex =
                                      updatedSubColumns.findIndex(
                                        (c) => c.key === col.key
                                      );
                                    if (colIndex !== -1) {
                                      updatedSubColumns[colIndex].order =
                                        index + 1;
                                    }
                                  });
                              }

                              setNewItem({
                                ...newItem,
                                subColumns: updatedSubColumns,
                              });
                            }}
                          />
                          {/* Label */}
                          <input
                            type="text"
                            value={columnLabel}
                            onChange={(e) => {
                              const updatedSubColumns = [...newItem.subColumns];
                              updatedSubColumns[column.originalIndex].label =
                                e.target.value;
                              setNewItem({
                                ...newItem,
                                subColumns: updatedSubColumns,
                              });
                            }}
                            placeholder="Column label"
                            style={{
                              padding: "6px 8px",
                              borderRadius: "4px",
                              border: "1px solid #d1d5db",
                              fontSize: "12px",
                              width: "90%",
                            }}
                          />

                          {/* Field - Disabled input */}
                          <input
                            type="text"
                            value={column.key}
                            disabled
                            style={{
                              padding: "6px 8px",
                              borderRadius: "4px",
                              border: "1px solid #d1d5db",
                              fontSize: "12px",
                              backgroundColor: "#f3f4f6",
                              color: "#6b7280",
                              cursor: "not-allowed",
                              width: "90%",
                            }}
                          />

                          {/* Alignment Selector */}
                          <select
                            value={column.alignment || "left"}
                            onChange={(e) => {
                              const updatedSubColumns = [...newItem.subColumns];
                              updatedSubColumns[
                                column.originalIndex
                              ].alignment = e.target.value;
                              setNewItem({
                                ...newItem,
                                subColumns: updatedSubColumns,
                              });
                            }}
                            style={{
                              padding: "4px",
                              borderRadius: "4px",
                              border: "1px solid #d1d5db",
                              fontSize: "11px",
                              width: "75px",
                            }}
                          >
                            <option value="left">Left</option>
                            <option value="center">Center</option>
                            <option value="right">Right</option>
                          </select>

                          {/* Order */}
                          <select
                            value={column.order || 9999}
                            onChange={(e) => {
                              const newOrder = parseInt(e.target.value);
                              const updatedSubColumns = [...newItem.subColumns];

                              // Find if another column has this order
                              const existingColWithOrder =
                                updatedSubColumns.find(
                                  (c) =>
                                    c.order === newOrder && c.key !== column.key
                                );

                              if (existingColWithOrder) {
                                // Swap orders
                                existingColWithOrder.order = column.order;
                              }

                              updatedSubColumns[column.originalIndex].order =
                                newOrder;
                              setNewItem({
                                ...newItem,
                                subColumns: updatedSubColumns,
                              });
                            }}
                            style={{
                              padding: "4px",
                              borderRadius: "4px",
                              border: "1px solid #d1d5db",
                              fontSize: "12px",
                            }}
                          >
                            {/* Show options for ALL sub-columns */}
                            {Array.from(
                              { length: newItem.subColumns.length },
                              (_, idx) => (
                                <option key={idx + 1} value={idx + 1}>
                                  {idx + 1}
                                </option>
                              )
                            )}
                          </select>
                        </div>
                      );
                    })}
                </div>
              </div>
            )}
        </>
      )}

      {/* Date Filter Section - UPDATED WITH DATE FIELD SELECTOR */}
      <div style={{ marginBottom: "24px" }}>
        <label
          style={{
            display: "block",
            marginBottom: "16px",
            fontSize: "16px",
            fontWeight: "bold",
            color: "#374151",
          }}
        >
          Data filtering based on date range
        </label>

        <div
          style={{
            display: "flex",
            gap: "16px",
            alignItems: "flex-start", // Changed from "flex-end" to "flex-start"
            flexWrap: "wrap",
          }}
        >
          {/* Date Field Selector Dropdown */}
          <div style={{ flex: 1, minWidth: "180px" }}>
            <label
              style={{
                display: "block",
                marginBottom: "8px",
                fontSize: "14px",
                fontWeight: "bold",
                color: "#374151",
              }}
            >
              Date Field
            </label>
            <select
              value={newItem.selectedDateField || ""}
              onChange={(e) => {
                setNewItem({
                  ...newItem,
                  selectedDateField: e.target.value,
                  dateFilterApplied: false,
                  dateFieldsChanged: true,
                  fromDate: "", // Clear fromDate
                  toDate: "", // Clear toDate
                  tableDateValidationError: null,
                  tableToDateValidationError: null,
                });
                setNoDataForDateRange(false);
              }}
              style={{
                width: "100%",
                padding: "10px 12px",
                borderRadius: "6px",
                border: "1px solid #d1d5db",
                fontSize: "14px",
                height: "40px",
                boxSizing: "border-box",
                backgroundColor: "white",
              }}
            >
              <option value="">Select date field</option>
              {dateFields.map((field) => (
                <option key={field.fullName} value={field.fullName}>
                  {field.label}
                </option>
              ))}
            </select>

            {/* Reserved space for consistency */}
            <div
              style={{
                height: "32px", // Fixed height to prevent layout shifts
                display: "flex",
                alignItems: "flex-start",
                paddingTop: "4px",
              }}
            >
              {/* Empty space for consistency */}
            </div>
          </div>

          {/* From Date - keep your existing code with reserved space */}
          <div style={{ flex: 1, minWidth: "150px" }}>
            <label
              style={{
                display: "block",
                marginBottom: "8px",
                fontSize: "14px",
                fontWeight: "bold",
                color: "#374151",
              }}
            >
              From Date
            </label>
            <input
              type="date"
              value={newItem.fromDate || ""}
              disabled={!newItem.selectedDateField}
              onChange={(e) => {
                // Your existing onChange logic here
                const selectedDate = e.target.value;
                const today = new Date().toISOString().split("T")[0];

                if (selectedDate > today) {
                  const updatedItem = {
                    ...newItem,
                    fromDate: today,
                    dateFieldsChanged: true,
                    tableDateValidationError:
                      "Future dates are not allowed. Date has been set to today.",
                  };

                  if (newItem.toDate && today > newItem.toDate) {
                    updatedItem.toDate = "";
                    updatedItem.tableToDateValidationError =
                      "To Date cleared because From Date was adjusted to today.";
                  }

                  setNewItem(updatedItem);

                  setTimeout(() => {
                    setNewItem((prev) => ({
                      ...prev,
                      tableDateValidationError: null,
                    }));
                  }, 3000);

                  return;
                }

                const updatedItem = {
                  ...newItem,
                  fromDate: selectedDate,
                  tableDateValidationError: null,
                  dateFilterApplied: false,
                  dateFieldsChanged: true,
                };

                if (newItem.toDate && newItem.toDate < selectedDate) {
                  updatedItem.toDate = "";
                  updatedItem.tableToDateValidationError =
                    "To Date cleared because it was earlier than From Date.";
                }

                setNewItem(updatedItem);
                setNoDataForDateRange(false);
              }}
              max={new Date().toISOString().split("T")[0]}
              style={{
                width: "100%",
                padding: "10px 12px",
                borderRadius: "6px",
                border: newItem.tableDateValidationError
                  ? "1px solid #ef4444"
                  : "1px solid #d1d5db",
                fontSize: "14px",
                height: "40px",
                boxSizing: "border-box",
                backgroundColor: !newItem.selectedDateField
                  ? "#f3f4f6"
                  : "white",
                cursor: !newItem.selectedDateField ? "not-allowed" : "pointer",
              }}
              onFocus={(e) => (e.target.style.borderColor = "#1a7a73")}
              onBlur={(e) =>
                (e.target.style.borderColor = newItem.tableDateValidationError
                  ? "#ef4444"
                  : "#d1d5db")
              }
            />

            {/* Reserved space for error message */}
            <div
              style={{
                height: "32px",
                display: "flex",
                alignItems: "flex-start",
                paddingTop: "4px",
              }}
            >
              {newItem.tableDateValidationError && (
                <span
                  style={{
                    color: "#ef4444",
                    fontSize: "12px",
                    lineHeight: "16px",
                  }}
                >
                  {newItem.tableDateValidationError}
                </span>
              )}
            </div>
          </div>

          {/* To Date - keep your existing code with reserved space */}
          <div style={{ flex: 1, minWidth: "150px" }}>
            <label
              style={{
                display: "block",
                marginBottom: "8px",
                fontSize: "14px",
                fontWeight: "bold",
                color: "#374151",
              }}
            >
              To Date
            </label>
            <input
              type="date"
              value={newItem.toDate || ""}
              disabled={!newItem.selectedDateField}
              onChange={(e) => {
                // Your existing onChange logic here
                const selectedDate = e.target.value;
                const today = new Date().toISOString().split("T")[0];

                if (selectedDate > today) {
                  setNewItem({
                    ...newItem,
                    toDate: today,
                    dateFieldsChanged: true,
                    tableToDateValidationError:
                      "Future dates are not allowed. Date has been set to today.",
                  });

                  setTimeout(() => {
                    setNewItem((prev) => ({
                      ...prev,
                      tableToDateValidationError: null,
                    }));
                  }, 3000);

                  return;
                }

                if (newItem.fromDate && selectedDate < newItem.fromDate) {
                  setNewItem({
                    ...newItem,
                    toDate: selectedDate,
                    dateFieldsChanged: true,
                    tableToDateValidationError:
                      "To Date cannot be earlier than From Date.",
                  });

                  return;
                }

                setNewItem({
                  ...newItem,
                  toDate: selectedDate,
                  dateFieldsChanged: true,
                  tableToDateValidationError: null,
                  tableDateValidationError: null,
                  dateFilterApplied: false,
                });
                setNoDataForDateRange(false);
              }}
              max={new Date().toISOString().split("T")[0]}
              // Remove min as discussed previously
              // min={newItem.fromDate || ""}
              style={{
                width: "100%",
                padding: "10px 12px",
                borderRadius: "6px",
                border: newItem.tableToDateValidationError
                  ? "1px solid #ef4444"
                  : "1px solid #d1d5db",
                fontSize: "14px",
                height: "40px",
                boxSizing: "border-box",
                backgroundColor: !newItem.selectedDateField
                  ? "#f3f4f6"
                  : "white",
                cursor: !newItem.selectedDateField ? "not-allowed" : "pointer",
              }}
              onFocus={(e) => (e.target.style.borderColor = "#1a7a73")}
              onBlur={(e) =>
                (e.target.style.borderColor = newItem.tableToDateValidationError
                  ? "#ef4444"
                  : "#d1d5db")
              }
            />

            {/* Reserved space for error message */}
            <div
              style={{
                height: "32px",
                display: "flex",
                alignItems: "flex-start",
                paddingTop: "4px",
              }}
            >
              {newItem.tableToDateValidationError && (
                <span
                  style={{
                    color: "#ef4444",
                    fontSize: "12px",
                    lineHeight: "16px",
                  }}
                >
                  {newItem.tableToDateValidationError}
                </span>
              )}
            </div>
          </div>

          {/* Update Filter Button - Positioned to align with input fields */}
          <div
            style={{
              display: "flex",
              flexDirection: "column", // Stack label and button
              justifyContent: "flex-start",
            }}
          >
            {/* Invisible label for alignment */}
            <div style={{ height: "24px", marginBottom: "8px" }}></div>

            <button
              onClick={handleRefreshWithDateFilter}
              disabled={
                !newItem.dataSource ||
                !newItem.fromDate ||
                !newItem.toDate ||
                !newItem.selectedDateField ||
                newItem.tableDateValidationError || // Disable if from date has validation error
                newItem.tableToDateValidationError // Disable if to date has validation error
              }
              style={{
                padding: "12px 20px",
                borderRadius: "6px",
                border: "1px solid #d1d5db",
                backgroundColor:
                  !newItem.dataSource ||
                  !newItem.fromDate ||
                  !newItem.toDate ||
                  !newItem.selectedDateField ||
                  newItem.tableDateValidationError ||
                  newItem.tableToDateValidationError
                    ? "#f3f4f6"
                    : "#1a7a73",
                color:
                  !newItem.dataSource ||
                  !newItem.fromDate ||
                  !newItem.toDate ||
                  !newItem.selectedDateField ||
                  newItem.tableDateValidationError ||
                  newItem.tableToDateValidationError
                    ? "#9ca3af"
                    : "white",
                cursor:
                  !newItem.dataSource ||
                  !newItem.fromDate ||
                  !newItem.toDate ||
                  !newItem.selectedDateField ||
                  newItem.tableDateValidationError ||
                  newItem.tableToDateValidationError
                    ? "not-allowed"
                    : "pointer",
                display: "flex",
                alignItems: "center",
                gap: "8px",
                fontSize: "14px",
                fontWeight: "500",
                height: "40px",
                transition: "all 0.2s ease",
                whiteSpace: "nowrap",
              }}
              title={
                newItem.tableDateValidationError ||
                newItem.tableToDateValidationError
                  ? "Please fix date validation errors before applying filter"
                  : "Apply date filter and refresh data"
              }
            >
              Update Filter
            </button>

            {/* Reserved space to match other fields */}
            <div style={{ height: "32px" }}></div>
          </div>

          {/* Rest of your existing validation messages and warnings remain the same */}
          {/* Warning message for unapplied date filters */}
          {newItem.fromDate &&
            newItem.toDate &&
            newItem.selectedDateField &&
            !newItem.dateFilterApplied &&
            newItem.dateFieldsChanged &&
            !newItem.tableDateValidationError &&
            !newItem.tableToDateValidationError &&
            !noDataForDateRange && (
              <div
                style={{
                  marginTop: "12px",
                  padding: "12px 16px",
                  backgroundColor: "#fef3c7",
                  border: "1px solid #f59e0b",
                  borderRadius: "6px",
                  color: "#92400e",
                  fontSize: "14px",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  width: "100%",
                }}
              >
                <AlertTriangle size={16} />
                Click "Update Filter" to apply the date range before saving the
                widget.
              </div>
            )}
        </div>

        {/* Keep your existing validation error messages below the flex container */}
        {/* Date Range Validation Error */}
        {newItem.fromDate && !newItem.toDate && (
          <div
            style={{
              marginTop: "12px",
              padding: "12px 16px",
              backgroundColor: "#fef2f2",
              border: "1px solid #fecaca",
              borderRadius: "6px",
              color: "#dc2626",
              fontSize: "14px",
              display: "flex",
              alignItems: "center",
              gap: "8px",
            }}
          >
            <AlertTriangle size={16} />
            Please select both From Date and To Date to create a valid date
            range for the table.
          </div>
        )}

        {/* Date Field Selection Validation Error */}
        {newItem.fromDate && newItem.toDate && !newItem.selectedDateField && (
          <div
            style={{
              marginTop: "12px",
              padding: "12px 16px",
              backgroundColor: "#fef2f2",
              border: "1px solid #fecaca",
              borderRadius: "6px",
              color: "#dc2626",
              fontSize: "14px",
              display: "flex",
              alignItems: "center",
              gap: "8px",
            }}
          >
            <AlertTriangle size={16} />
            Please select a date field to filter by.
          </div>
        )}
      </div>
      {noDataForDateRange && (
        <div
          style={{
            marginTop: "12px",
            padding: "12px 16px",
            backgroundColor: "#f0f9ff",
            border: "1px solid #1a7a73",
            borderRadius: "6px",
            color: "#0f4a45",
            fontSize: "14px",
            display: "flex",
            alignItems: "center",
            gap: "8px",
          }}
        >
          <AlertTriangle size={16} />
          No data found for the selected date range.
        </div>
      )}

      {/* Other table options remain the same */}
      <div>
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: "16px",
            marginTop: "16px",
          }}
        >
          {/* Left side options */}
          <div style={{ flex: 1, minWidth: "200px" }}>
            <label
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                marginBottom: "8px",
                fontSize: "14px",
                fontWeight: "500",
                color: newItem.dateFilterApplied ? "#374151" : "#9ca3af", // Dimmed when disabled
                cursor: newItem.dateFilterApplied ? "pointer" : "not-allowed",
              }}
            >
              <input
                type="checkbox"
                checked={newItem.showDateFilter || false}
                disabled={!newItem.dateFilterApplied} // Disable when date filter hasn't been applied
                onChange={(e) => {
                  // Only allow changes if date filter has been applied
                  if (newItem.dateFilterApplied) {
                    setNewItem({
                      ...newItem,
                      showDateFilter: e.target.checked,
                    });
                  }
                }}
                style={{
                  width: "16px",
                  height: "16px",
                  accentColor: "#1a7a73",
                  cursor: newItem.dateFilterApplied ? "pointer" : "not-allowed",
                  opacity: newItem.dateFilterApplied ? 1 : 0.5, // Visual indication of disabled state
                }}
              />
              Enable Date Range Filter
              {!newItem.dateFilterApplied && (
                <span
                  style={{
                    fontSize: "12px",
                    color: "#6b7280",
                    fontStyle: "italic",
                  }}
                >
                  (Apply date filter first)
                </span>
              )}
            </label>
            <label
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                marginBottom: "8px",
                fontSize: "14px",
                fontWeight: "500",
                color: "#374151",
                cursor: "pointer",
              }}
            >
              <input
                type="checkbox"
                checked={newItem.showDownload || false}
                onChange={(e) =>
                  setNewItem({
                    ...newItem,
                    showDownload: e.target.checked,
                  })
                }
                style={{
                  width: "16px",
                  height: "16px",
                  accentColor: "#1a7a73",
                  cursor: "pointer",
                }}
              />
              Include Download Excel Button
            </label>
            {/* NEW PDF Download Checkbox */}
            <label
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                marginBottom: "8px",
                fontSize: "14px",
                fontWeight: "500",
                color: "#374151",
                cursor: "pointer",
              }}
            >
              <input
                type="checkbox"
                checked={newItem.showPDFDownload || false}
                onChange={(e) =>
                  setNewItem({
                    ...newItem,
                    showPDFDownload: e.target.checked,
                  })
                }
                style={{
                  width: "16px",
                  height: "16px",
                  accentColor: "#1a7a73",
                  cursor: "pointer",
                }}
              />
              Include Download PDF Button
            </label>

            <label
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                marginBottom: "8px",
                fontSize: "14px",
                fontWeight: "500",
                color: "#374151",
                cursor: "pointer",
              }}
            >
              <input
                type="checkbox"
                checked={newItem.showSearch || false}
                onChange={(e) =>
                  setNewItem({
                    ...newItem,
                    showSearch: e.target.checked,
                  })
                }
                style={{
                  width: "16px",
                  height: "16px",
                  accentColor: "#1a7a73",
                  cursor: "pointer",
                }}
              />
              Include Search Box
            </label>
            {/* Add this new checkbox after the existing checkboxes */}
            {/* {userRole !== "SUPER ADMIN" && ( */}
            <label
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                marginBottom: "8px",
                fontSize: "14px",
                fontWeight: "500",
                color: "#374151",
                cursor: "pointer",
              }}
            >
              <input
                type="checkbox"
                checked={newItem.showBankLogo || false}
                onChange={(e) =>
                  setNewItem({
                    ...newItem,
                    showBankLogo: e.target.checked,
                  })
                }
                style={{
                  width: "16px",
                  height: "16px",
                  accentColor: "#1a7a73",
                  cursor: "pointer",
                }}
              />
              Include Bank/Branch Logo in Downloads
            </label>
            {/* )} */}
          </div>

          {/* Right side options */}
          <div style={{ flex: 1, minWidth: "200px" }}>
            <label
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                marginBottom: "8px",
                fontSize: "14px",
                fontWeight: "500",
                color: "#374151",
                cursor: "pointer",
              }}
            >
              <input
                type="checkbox"
                checked={newItem.showPagination || false}
                onChange={(e) =>
                  setNewItem({
                    ...newItem,
                    showPagination: e.target.checked,
                  })
                }
                style={{
                  width: "16px",
                  height: "16px",
                  accentColor: "#1a7a73",
                  cursor: "pointer",
                }}
              />
              Enable Pagination
            </label>

            <label
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                marginBottom: "8px",
                fontSize: "14px",
                fontWeight: "500",
                color: "#374151",
                cursor: "pointer",
              }}
            >
              <input
                type="checkbox"
                checked={newItem.enableSorting || false}
                onChange={(e) =>
                  setNewItem({
                    ...newItem,
                    enableSorting: e.target.checked,
                  })
                }
                style={{
                  width: "16px",
                  height: "16px",
                  accentColor: "#1a7a73",
                  cursor: "pointer",
                }}
              />
              Enable Column Sorting
            </label>
            {/* PDF Title Input Field - Only show when PDF download is enabled */}

            <div style={{ marginTop: "12px" }}>
              {/* Enable PDF Title Checkbox */}
              <label
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  marginBottom: 8,
                  fontSize: 14,
                  fontWeight: 500,
                  color: "#374151",
                  cursor: "pointer",
                }}
              >
                <input
                  type="checkbox"
                  checked={newItem.enablePDFTitle || false}
                  onChange={(e) => {
                    const isEnabled = e.target.checked;
                    setNewItem({
                      ...newItem,
                      enablePDFTitle: isEnabled,
                      // Clear the title when checkbox is unchecked
                      pdfTitle: isEnabled ? newItem.pdfTitle : "",
                    });
                  }}
                  style={{
                    width: 16,
                    height: 16,
                    accentColor: "#1a7a73",
                    cursor: "pointer",
                  }}
                />
                Enable PDF/Excel Title
              </label>

              {/* PDF Title Input Field - Only show when enabled */}
              {newItem.enablePDFTitle && (
                <>
                  <label
                    style={{
                      display: "block",
                      marginBottom: 8,
                      fontSize: 14,
                      fontWeight: 500,
                      color: "#374151",
                      marginLeft: 24,
                    }}
                  >
                    PDF/Excel Title
                  </label>
                  <input
                    type="text"
                    value={newItem.pdfTitle || ""}
                    onChange={(e) =>
                      setNewItem({
                        ...newItem,
                        pdfTitle: e.target.value,
                      })
                    }
                    placeholder="Enter PDF/Excel title"
                    maxLength={100}
                    style={{
                      width: "90%",
                      padding: "8px 12px",
                      borderRadius: 6,
                      border: "1px solid #d1d5db",
                      fontSize: 14,
                      outline: "none",
                      transition: "border-color 0.2s",
                      marginLeft: 24,
                    }}
                    onFocus={(e) => (e.target.style.borderColor = "#1a7a73")}
                    onBlur={(e) => (e.target.style.borderColor = "#d1d5db")}
                  />
                  <div
                    style={{
                      fontSize: 12,
                      color: "#6b7280",
                      marginTop: 4,
                      marginLeft: 24,
                    }}
                  >
                    Character count: {(newItem.pdfTitle || "").length}/100
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default TableEditForm;
